import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import schedulePageActions from 'store/schedule/schedulePageActions';

/**
 * Handles selection state for workers and equipment on a given sheet.
 *
 * Keeps ScheduleEventCardV6 simple and moves selection logic into a hook.
 */
export const useScheduleEventSelection = (sheetId) => {
  const dispatch = useDispatch();
  const selectedResources = useSelector((state) => state.schedule.selectedResources || {});

  const isCurrentSheetSelected = selectedResources.sheetId === sheetId;

  const selectedWorkers = useMemo(
    () => (isCurrentSheetSelected ? selectedResources.workers || [] : []),
    [isCurrentSheetSelected, selectedResources.workers]
  );

  const selectedEquipment = useMemo(
    () => (isCurrentSheetSelected ? selectedResources.equipment || [] : []),
    [isCurrentSheetSelected, selectedResources.equipment]
  );

  const selectWorklog = useCallback(() => {
    if (!isCurrentSheetSelected) {
      dispatch(schedulePageActions.setSelectedResourcesWorklogId(sheetId));
    }
  }, [dispatch, isCurrentSheetSelected, sheetId]);

  const onWorkerClick = useCallback(
    (workerId) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedResources.sheetId && selectedResources.sheetId !== sheetId) return;
      selectWorklog();
      dispatch(schedulePageActions.selectWorklogPeopleResource(workerId));
    },
    [dispatch, selectWorklog, selectedResources.sheetId, sheetId]
  );

  const onEquipmentClick = useCallback(
    (equipmentId) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedResources.sheetId && selectedResources.sheetId !== sheetId) return;
      selectWorklog();
      dispatch(schedulePageActions.selectWorklogEquipmentResource(equipmentId));
    },
    [dispatch, selectWorklog, selectedResources.sheetId, sheetId]
  );

  return {
    selectedWorkers,
    selectedEquipment,
    onWorkerClick,
    onEquipmentClick,
  };
};

export default useScheduleEventSelection;

